import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';
import { CURRENCY_CONFIGS, SORTED_CURRENCIES } from '../utils/currencyConfigs';

const DualRangeSlider = ({ value, onChange, disabled, onConfirm }) => {
    const [localValue, setLocalValue] = useState(() => {
        const initialValue = typeof value === 'string' ? JSON.parse(value) : value;
        return initialValue || {
            min: 0,
            max: 100000,
            currency: '$',
            currencyCode: 'USD',
            isConfirmed: false
        };
    });
    const [isDragging, setIsDragging] = useState(null);
    const sliderRef = useRef(null);
    const previousCurrencyRef = useRef(localValue.currencyCode);

    // Determine if showing confirmation based on value state
    const showConfirmation = useMemo(() => {
        if (!value && !localValue.isConfirmed) return true;
        return false;
    }, [value, localValue.isConfirmed]);

    const currencyConfig = useMemo(() => 
        CURRENCY_CONFIGS[localValue.currencyCode] || CURRENCY_CONFIGS.USD,
        [localValue.currencyCode]
    );

    const notifyChange = useCallback((updatedValue, skipDebounce = false) => {
        const config = CURRENCY_CONFIGS[updatedValue.currencyCode] || CURRENCY_CONFIGS.USD;
        const step = config.step || 1000;
        
        const formattedValue = {
            min: Math.round(Number(updatedValue.min) / step) * step,
            max: Math.round(Number(updatedValue.max) / step) * step,
            currency: config.symbol,
            currencyCode: updatedValue.currencyCode,
            isConfirmed: true,
            usdMin: Math.round(updatedValue.min / config.scale),
            usdMax: Math.round(updatedValue.max / config.scale)
        };

        formattedValue.min = Math.max(config.min, Math.min(formattedValue.min, config.max));
        formattedValue.max = Math.max(config.min, Math.min(formattedValue.max, config.max));
        
        if (formattedValue.min > formattedValue.max) {
            formattedValue.min = formattedValue.max;
        }

        if (skipDebounce) {
            onChange(formattedValue);
        } else {
            debouncedOnChange(formattedValue);
        }
    }, [onChange]);

    const debouncedOnChange = useMemo(() => 
        debounce(notifyChange, 500),
        [notifyChange]
    );

    const getValueFromPosition = useCallback((position) => {
        if (!sliderRef.current) return 0;
        
        const slider = sliderRef.current;
        const rect = slider.getBoundingClientRect();
        const percentage = (position - rect.left) / rect.width;
        const range = currencyConfig.max - currencyConfig.min;
        const value = currencyConfig.min + (range * percentage);
        const stepped = Math.round(value / currencyConfig.step) * currencyConfig.step;
        
        return Math.max(currencyConfig.min, Math.min(stepped, currencyConfig.max));
    }, [currencyConfig]);

    const handleMouseDown = useCallback((e, handle) => {
        if (disabled) return;
        e.preventDefault();
        setIsDragging(handle);
    }, [disabled]);

    const handleMouseMove = useCallback((e) => {
        if (!isDragging || disabled) return;

        const newValue = getValueFromPosition(e.clientX);
        setLocalValue(prev => {
            const updated = {
                ...prev,
                [isDragging]: newValue,
                isConfirmed: true
            };
            if (isDragging === 'min' && newValue > prev.max) {
                updated.min = prev.max;
            }
            if (isDragging === 'max' && newValue < prev.min) {
                updated.max = prev.min;
            }
            debouncedOnChange(updated);
            return updated;
        });
    }, [isDragging, disabled, getValueFromPosition, debouncedOnChange]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(null);
    }, []);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    const handleCurrencyChange = useCallback((e) => {
        if (disabled) return;

        const newCurrencyCode = e.target.value;
        const newConfig = CURRENCY_CONFIGS[newCurrencyCode];
        
        if (!newConfig) return;

        // Get the old and new scales for conversion
        const oldConfig = CURRENCY_CONFIGS[previousCurrencyRef.current];
        const oldToUSD = 1 / oldConfig.scale;
        const USDToNew = newConfig.scale;
        const conversionRate = oldToUSD * USDToNew;

        setLocalValue(prev => {
            // Convert values using USD as intermediate
            const updatedValue = {
                ...prev,
                min: Math.round(prev.min * conversionRate),
                max: Math.round(prev.max * conversionRate),
                currency: newConfig.symbol,
                currencyCode: newCurrencyCode,
                isConfirmed: true
            };

            // Ensure values stay within new currency's bounds
            updatedValue.min = Math.max(newConfig.min, Math.min(updatedValue.min, newConfig.max));
            updatedValue.max = Math.max(newConfig.min, Math.min(updatedValue.max, newConfig.max));

            // Update reference for next conversion
            previousCurrencyRef.current = newCurrencyCode;

            // Notify change immediately without debounce for currency changes
            notifyChange(updatedValue, true);

            return updatedValue;
        });
    }, [disabled, notifyChange]);

    const handleConfirm = useCallback(() => {
        const confirmed = {
            ...localValue,
            isConfirmed: true
        };
        setLocalValue(confirmed);
        notifyChange(confirmed, true);
        if (onConfirm) {
            onConfirm(confirmed);
        }
    }, [localValue, notifyChange, onConfirm]);

    const displayMin = useMemo(() => 
        Number(localValue.min).toLocaleString(), [localValue.min]
    );
    
    const displayMax = useMemo(() =>
        Number(localValue.max).toLocaleString(), [localValue.max]
    );

    const getLeftPosition = useCallback((value) => {
        const normalizedValue = ((value - currencyConfig.min) / (currencyConfig.max - currencyConfig.min)) * 100;
        return Math.max(0, Math.min(100, normalizedValue));
    }, [currencyConfig]);

    // Effect to sync with external value changes
    useEffect(() => {
        if (value && typeof value === 'string') {
            try {
                const parsedValue = JSON.parse(value);
                setLocalValue(prev => ({
                    ...prev,
                    ...parsedValue,
                    isConfirmed: true
                }));
                previousCurrencyRef.current = parsedValue.currencyCode;
            } catch (e) {
                console.error('Error parsing value:', e);
            }
        }
    }, [value]);

    return (
        <div className="space-y-6">
            <div className="flex justify-center items-center mb-8">
                <div className="flex items-center space-x-3">
                    <label className="text-base font-semibold text-gray-700">Currency:</label>
                    <select
                        value={localValue.currencyCode}
                        onChange={handleCurrencyChange}
                        disabled={disabled}
                        className="block w-56 px-4 py-2.5 text-lg bg-white border-2 border-gray-200 
                                  hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 
                                  focus:outline-none rounded-lg transition-colors duration-200 
                                  cursor-pointer font-medium text-gray-800"
                    >
                        {SORTED_CURRENCIES.map(currency => (
                            <option key={currency.code} value={currency.code}>
                                {currency.name} ({currency.symbol})
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                    <span>Min: {currencyConfig.symbol}{displayMin}</span>
                    <span>Max: {currencyConfig.symbol}{displayMax}</span>
                </div>

                <div className="relative w-full h-4 max-w-full" ref={sliderRef}>
                    {/* Track background */}
                    <div className="absolute w-full h-full bg-gray-200 rounded-full" />
                    
                    {/* Selected range */}
                    <div 
                        className="absolute h-full bg-blue-500 rounded-full transition-all duration-150"
                        style={{
                            left: `${getLeftPosition(localValue.min)}%`,
                            right: `${100 - getLeftPosition(localValue.max)}%`
                        }}
                    />

                    {/* Min handle */}
                    <div
                        className={`absolute w-6 h-6 -ml-3 -mt-1 bg-white border-2 border-blue-500 rounded-full cursor-pointer transition-transform duration-150 ${
                            isDragging === 'min' ? 'ring-2 ring-blue-200' : ''
                        }`}
                        style={{ left: `${getLeftPosition(localValue.min)}%` }}
                        onMouseDown={(e) => handleMouseDown(e, 'min')}
                    />

                    {/* Max handle */}
                    <div
                        className={`absolute w-6 h-6 -ml-3 -mt-1 bg-white border-2 border-blue-500 rounded-full cursor-pointer transition-transform duration-150 ${
                            isDragging === 'max' ? 'ring-2 ring-blue-200' : ''
                        }`}
                        style={{ left: `${getLeftPosition(localValue.max)}%` }}
                        onMouseDown={(e) => handleMouseDown(e, 'max')}
                    />
                </div>
            </div>

            {showConfirmation && (
                <div className="flex flex-col gap-2">
                    <p className="text-sm text-gray-500">
                        Please confirm your budget range before continuing.
                        Default values will be used if left unchanged.
                    </p>

                    <div className="flex justify-end">
                        <button
                            onClick={handleConfirm}
                            disabled={disabled}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                        >
                            Confirm Values
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default React.memo(DualRangeSlider);